import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store";
import { fetchMyGifts } from "../../../../Redux/luckydraw/slice";
import GiftsTable from "./GiftsTable";
import { toast } from "react-toastify";
import {
  ChevronLeft,
  ChevronRight,
  Gift,
  Search,
  X,
} from "lucide-react";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import "./GiftsPage.css";

const Gifts = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { myGifts, loading, error } = useSelector(
    (state: RootState) => state.luckyDraw,
  );

  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchMyGifts());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const filteredGifts = myGifts.filter((gift) => {
    const term = searchValue.trim().toLowerCase();
    const luckyDrawName =
      typeof gift.luckyDrawId === "object"
        ? gift.luckyDrawId?.name || ""
        : "";
    const giftName =
      typeof gift.giftId === "object" && gift.giftId !== null
        ? gift.giftId?.name || ""
        : "";
    const matchesSearch =
      !term ||
      luckyDrawName.toLowerCase().includes(term) ||
      giftName.toLowerCase().includes(term);
    const wonDate = gift.wonAt || "";
    const matchesFrom = !dateRange.from || wonDate >= dateRange.from;
    const matchesTo = !dateRange.to || wonDate <= dateRange.to;
    return matchesSearch && matchesFrom && matchesTo;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredGifts.length / pageSize),
  );
  const page = Math.min(currentPage, totalPages);
  const visibleGifts = filteredGifts.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  return (
    <section className="gifts-page">
      <div className="gifts-shell">
        <header className="gifts-header">
          <div className="gifts-heading">
            <span className="gifts-heading-icon">
              <Gift size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="gifts-kicker">MY REWARDS</span>
              <h1>My Won Gifts</h1>
              <p>
                Track achievement rewards, referral incentives, gift
                credits and payout receipts.
              </p>
            </div>
          </div>
          <span className="gifts-count-chip">
            {filteredGifts.length} gifts
          </span>
        </header>

        <div className="gifts-toolbar">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            aria-label="Gifts per page"
          >
            <option value={5}>5 Entries</option>
            <option value={10}>10 Entries</option>
            <option value={20}>20 Entries</option>
          </select>

          <div className="gifts-date-picker">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <div className="gifts-search">
            <Search size={17} strokeWidth={2} />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search gifts..."
              aria-label="Search gifts"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => setSearchValue("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {loading.myGifts ? (
          <div className="gifts-desktop-empty-cell">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <GiftsTable
            gifts={visibleGifts}
            startIndex={(page - 1) * pageSize}
          />
        )}


        {totalPages > 1 && (
          <footer className="gifts-pagination">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setCurrentPage(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>
            <span>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setCurrentPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </footer>
        )}
      </div>
    </section>
  );
};

export default Gifts;
